import { useAuth0 } from "@auth0/auth0-react"
import * as Sentry from "@sentry/react"
import classNames from "classnames"
import "primeflex/primeflex.css"
import "primeicons/primeicons.css"
import "styles/primeflex-compat.css"
import React, { Suspense, useEffect, useState, createContext } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import AppMenu from "components/_layout/AppMenu"
import AppTopBar from "components/_layout/AppTopbar"
import MaintenanceNotice from "components/_layout/MaintenanceNotice"
import { getRoutes } from "utils/routes"
import { homePathFor, isPathForRoutes, openableRoutes, routeForPath } from "utils/routes/paths"
import { useQueryClient } from "@tanstack/react-query"
import { IDLE_PRELOAD_PATHS, RoutePreloadProvider, preloadRouteForPath, useIdleRoutePreload } from "utils/routes/preload"
import { getMenu } from "utils/menu"
import Loading from "components/loading"
import { useTheme } from "context/theme-provider"
import { useCustomerData } from "context/customer-provider"
import { useNRControlDesk } from "context/control-desk-provider"
import useAuth0Metadata from "api/useAuth0Metadata"
import useMySettings from "api/useMySettings"
import { initFullStory } from "analytics"
import { RouteViewFiltersProvider } from "pages/roads/RouteView/RouteViewFilters/RouteViewFiltersContext"
import { DraftPackageProvider } from "context/draft-package-provider"
import useMyDevicePageRedirect from "hooks/useMobileRedirect"
import { useRecordLastOpened } from "hooks/useRecordLastOpened"
import {
  MENU_BUTTON_SELECTOR,
  SIDEBAR_ID,
  intercomLauncherHidden,
  useIsDesktopMenu,
  useMenuPeek,
  useMenuToggleShortcut,
} from "components/_layout/compactMenu"

export const MenuContext = createContext(false)
const App = () => {
  const { customerData, customerId } = useCustomerData()
  const { userIsSuperAdmin, updateUserMetadata } = useAuth0Metadata()
  const mySettings = useMySettings()
  const { controlDesk, controlDesks } = useNRControlDesk()
  const { user } = useAuth0()

  const { theme } = useTheme()

  const userEmail = user?.email || ""
  const { customerTitle, product } = customerData!

  const location = useLocation()
  // only the routes this user can open at this customer (not disabled;
  // internal ones for super-admins); the guard below matches on the first
  // path segment, disregarding dynamic segments
  const routes = openableRoutes(getRoutes(customerId, userIsSuperAdmin, controlDesks), !!userIsSuperAdmin)
  // A path this customer's app has no page for (a roads path after switching
  // to Network Rail, a stale bookmark) goes to its home — "/" or
  // "/dashboard" — never to a page that renders blank.
  const homePath = homePathFor(routes)

  // Page code ahead of need: on menu hover (RoutePreloadProvider → AppSubmenu)
  // and, once idle, for the pages most sessions open next.
  const preloadPath = (pathname: string) => preloadRouteForPath(pathname, routes)

  // The opened page's first requests start with its code download (a route's
  // preloadData), not after the chunk has arrived and rendered.
  const queryClient = useQueryClient()
  useEffect(() => {
    routeForPath(location.pathname, routes)?.preloadData?.({ queryClient, customerId, search: location.search })
    // once per page opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, customerId])
  useIdleRoutePreload(
    (homePath === "/" ? IDLE_PRELOAD_PATHS.rail : IDLE_PRELOAD_PATHS.roads).filter((path) => path !== location.pathname && isPathForRoutes(path, routes)),
    routes,
  )
  const menu = getMenu(
    customerId,
    userIsSuperAdmin,
    controlDesk,
    customerData,
  )

  // keyed on the account's fields, not the user object (replaced mid-load by
  // the boot snapshot handover and the token renewal)
  const userSub = user?.sub
  const userName = user?.name
  useEffect(() => {
    if (userSub) {
      initFullStory(userEmail, {
        customerid: customerId,
        name: userName,
        email: userEmail,
        product: product || "roads",
      })

      Sentry.setUser({ email: userEmail })
    }
  }, [userSub, userName, customerId, userEmail, product])

  useRecordLastOpened(userSub, updateUserMetadata)

  const [menuActive, setMenuActive] = useState(false)
  const [overlayMenuActive, setOverlayMenuActive] = useState(false)
  // "Compact": the side menu tucked away for more page space (☰ or [). Kept
  // in memory only — survives moving between pages, a reload brings it back.
  const [staticMenuDesktopInactive, setStaticMenuDesktopInactive] = useState(false)
  // While compact, the menu slides in over the page from the left edge
  const [menuPeekOpen, setMenuPeekOpen] = useState(false)
  // The theme's static sidebar only exists from 992px; below that it is the
  // mobile overlay menu, whatever the compact state says.
  const desktopMenu = useIsDesktopMenu()
  const menuHidden = staticMenuDesktopInactive && desktopMenu
  const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false)
  const [topbarUserMenuActive, setTopbarUserMenuActive] = useState(false)
  const [configActive, setConfigActive] = useState(false)

  let menuClick = false
  let userMenuClick = false
  let configClick = false

  const menuMode = "static"

  useEffect(() => {
    if (staticMenuMobileActive) {
      blockBodyScroll()
    } else {
      unblockBodyScroll()
    }
  }, [staticMenuMobileActive])

  // Mobile redirect for users on mobile devices
  // ART-3182: never redirect on the transient profile value — wait for the per-customer answer
  useMyDevicePageRedirect(mySettings.settled && !!mySettings.vehicle)

  const onDocumentClick = () => {
    if (!userMenuClick) {
      setTopbarUserMenuActive(false)
    }

    if (!menuClick) {
      if (overlayMenuActive || staticMenuMobileActive) {
        hideOverlayMenu()
      }

      unblockBodyScroll()
    }

    if (configActive && !configClick) {
      setConfigActive(false)
    }

    configClick = false
    menuClick = false
  }

  const onMenuClick = () => {
    menuClick = true
  }

  // ☰ / [ — hide or show the menu. A click during a peek pins the menu open.
  const toggleCompactMenu = () => {
    const hiding = !staticMenuDesktopInactive
    // focus must not stay on a link in a menu that is sliding off-screen
    if (hiding && document.getElementById(SIDEBAR_ID)?.contains(document.activeElement)) {
      document.querySelector<HTMLElement>(MENU_BUTTON_SELECTOR)?.focus()
    }
    setStaticMenuDesktopInactive(hiding)
    setMenuPeekOpen(false)
  }
  useMenuToggleShortcut(toggleCompactMenu)
  useMenuPeek({
    enabled: menuHidden,
    open: menuPeekOpen,
    onOpen: () => setMenuPeekOpen(true),
    onClose: () => setMenuPeekOpen(false),
  })
  // a peek never outlives the page it was opened on
  useEffect(() => {
    setMenuPeekOpen(false)
  }, [location.pathname])
  // the chat launcher leaves the corner when the menu becomes a drawer
  useEffect(() => {
    if (typeof window.Intercom === "function") window.Intercom("update", { hide_default_launcher: intercomLauncherHidden() })
  }, [desktopMenu])
  const onChatClick = () => {
    hideOverlayMenu()
    if (typeof window.Intercom === "function") window.Intercom("show")
  }

  const onMenuButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    menuClick = true
    setTopbarUserMenuActive(false)

    if (desktopMenu) {
      toggleCompactMenu()
    } else {
      setStaticMenuMobileActive((prevStaticMenuMobileActive) => !prevStaticMenuMobileActive)
    }

    e.preventDefault()
  }

  // TODO: better type for e
  const onMenuitemClick = (e: any) => {
    if (!e.item.items) {
      hideOverlayMenu()
      setMenuPeekOpen(false)
    }
  }

  const onRootMenuitemClick = () => {
    setMenuActive((prevMenuActive) => !prevMenuActive)
  }

  const onTopbarUserMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    userMenuClick = true
    setTopbarUserMenuActive((prevTopbarUserMenuActive) => !prevTopbarUserMenuActive)

    hideOverlayMenu()

    event.preventDefault()
  }

  const hideOverlayMenu = () => {
    setOverlayMenuActive(false)
    setStaticMenuMobileActive(false)
    unblockBodyScroll()
  }

  const blockBodyScroll = () => {
    if (document.body.classList) {
      document.body.classList.add("blocked-scroll")
    } else {
      document.body.className += " blocked-scroll"
    }
  }

  const unblockBodyScroll = () => {
    if (document.body.classList) {
      document.body.classList.remove("blocked-scroll")
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(`(^|\\b)${"blocked-scroll".split(" ").join("|")}(\\b|$)`, "gi"),
        " ",
      )
    }
  }

  const containerClassName = classNames("layout-wrapper layout-static", {
    "layout-sidebar-dim": theme === "dim",
    "layout-sidebar-dark": theme === "dark",
    "layout-overlay-active": overlayMenuActive,
    "layout-mobile-active": staticMenuMobileActive,
    "layout-static-inactive": staticMenuDesktopInactive,
    "layout-menu-peek": menuHidden && menuPeekOpen,
  })

  return (
    <div
      className={containerClassName}
      data-theme={theme}
      onClick={onDocumentClick}
      onKeyPress={onDocumentClick}
      role="button"
      tabIndex={0}
    >
      <MenuContext.Provider value={!staticMenuDesktopInactive}>
        <RouteViewFiltersProvider>
          <DraftPackageProvider>
            {/* all widths: phones get the same shell (top bar, padding) as tablets */}
            <div className="layout-content-wrapper">
              {/* On phones too: without it there was no menu, customer switch or Log out below 769px */}
              <AppTopBar
                topbarUserMenuActive={topbarUserMenuActive}
                menuHidden={menuHidden}
                mobileMenuOpen={staticMenuMobileActive}
                onMenuButtonClick={onMenuButtonClick}
                onTopbarUserMenu={onTopbarUserMenuButtonClick}
              />
              {/* Below 992px; the topbar carries its own pill above that (CSS decides). */}
              <MaintenanceNotice variant="strip" />
              <Suspense fallback={<Loading />}>
                <div className="layout-content">
                  {isPathForRoutes(location.pathname, routes) ? (
                    <Routes>
                      {routes.map((route) => (
                        <Route key={`route:${route.path}`} path={route.path} element={<route.component />} />
                      ))}
                    </Routes>
                  ) : isPathForRoutes(homePath, routes) ? (
                    <Navigate to={{ pathname: homePath, search: "" }} replace />
                  ) : null}
                </div>
              </Suspense>
            </div>
          </DraftPackageProvider>
        </RouteViewFiltersProvider>

        <RoutePreloadProvider value={preloadPath}>
          <AppMenu
            model={menu}
            menuMode={menuMode}
            appName={customerTitle}
            active={menuActive}
            mobileMenuActive={staticMenuMobileActive}
            onMenuClick={onMenuClick}
            onMenuitemClick={onMenuitemClick}
            onRootMenuitemClick={onRootMenuitemClick}
            onChatClick={onChatClick}
          />
        </RoutePreloadProvider>

        <div className="layout-mask modal-in" />
      </MenuContext.Provider>
    </div>
  )
}

export default App
